import {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {omit, isEmpty} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';

import {getLocalizationService} from '~/shared/services/localisationService';
import {
  selectActiveFilters,
} from '~/shared/store/selectors';
import actions from '~/shared/store/actions';
import {trackEvent} from '~/shared/services/analytics';
import {EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {CuisinesFilterMethod} from '~/shared/store/storeModules/ui/uiReducer';
import {removeQueries, useRouteQuery} from '~/shared/router';
import {MiniFeedQueries} from '~/shared/consts/restaurantConsts';

import {ITenbisForBusinessCategory} from './interface';
import {categories, cuisineIdsToFilterBy} from './utils';
import TenbisForBusinessView from './TenbisForBusiness.view';
import Moments from './Moments';

const TenbisForBusiness = ({isMomentsEnabled}: {
  isMomentsEnabled: boolean;
}) => {
  const {currentLanguageKey} = getLocalizationService();

  const [isOpen, setIsOpen] = useState(false);
  const [activeBusinessCategory, setActiveBusinessCategory] = useState<ITenbisForBusinessCategory>();

  const dispatch = useDispatch();

  const activeFiltersWithLastActive = useSelector(selectActiveFilters);
  const query = useRouteQuery();

  useEffect(() => {
    if (isMomentsEnabled) {
      trackEvent('moments_view');
      return;
    }
    trackEvent('hasViewedOfficeSupplyFilter');
  }, [isMomentsEnabled]);

  const activeFilters = useMemo(
    () => omit(activeFiltersWithLastActive, 'lastFilterTypeSelected'),
    [activeFiltersWithLastActive],
  );

  useEffect(() => {
    if (isEmpty(activeFilters?.cuisines)) {
      setActiveBusinessCategory(undefined);
      return;
    }

    const cuisineKey = Object.keys(activeFilters?.cuisines || EMPTY_OBJECT)[0];
    if (cuisineIdsToFilterBy.includes(cuisineKey)) {
      const businessCategory = categories.find(cat => cat.cuisineIdsToFilterBy.includes(cuisineKey));
      if (businessCategory) {
        setActiveBusinessCategory(businessCategory);
        return;
      }
    }
    setActiveBusinessCategory(undefined);
  }, [activeFilters]);

  const toggleDropdown = () => {
    if (!isOpen) {
      trackEvent('hasOpenedOfficeSupplyFilter');
    }
    setIsOpen(!isOpen);
  };

  const closeDropdown = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  const onSelect = useCallback(
    (selectedCategory: ITenbisForBusinessCategory) => {
      closeDropdown();
      setActiveBusinessCategory(selectedCategory);
      trackEvent('hasClickedOfficeSupplyFilter', {officeSupplyOption: selectedCategory.id});

      if (query?.cuisine) {
        removeQueries([MiniFeedQueries.CUISINE]);
      }

      dispatch(actions.setCuisinesFilter({
        cuisines: selectedCategory?.cuisineIdsToFilterBy,
        cuisinesFilterMethod: CuisinesFilterMethod.OR,
      }));
    },
    [closeDropdown, dispatch, query],
  );

  const onReset = useCallback(() => {
    setActiveBusinessCategory(undefined);
    dispatch(actions.resetRestaurantsFilter());
    trackEvent('hasResetOfficeSupplyFilter');
  }, [dispatch]);

  if (isMomentsEnabled) {
    return (<Moments />);
  }

  return (
    <TenbisForBusinessView
      activeCategory={activeBusinessCategory}
      isOpen={isOpen}
      currentLanguageKey={currentLanguageKey}
      categories={categories}
      closeDropdown={closeDropdown}
      onReset={onReset}
      onSelect={onSelect}
      toggleDropdown={toggleDropdown}
    />
  );
};

export default memo(TenbisForBusiness);
